import React, { useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import { createUpdateCalendarLog } from '../objects/calendarLog';
import { parseDesc } from '../hooks/useEventos';
import { normISO, fmtHora, toInputValue, val, getDescriptivo } from '../utils/surgeryHelpers';
import useResources from '../hooks/useResources';

/* Compara nombres de sala/unidad ignorando espacios no separables, dobles y en los extremos */
const normTxt = (t) => String(t ?? '').normalize('NFC').replace(/[\u00a0\u2007\u202f]/g, ' ').replace(/\s+/g, ' ').trim();

/* Reemplaza solo el segmento AppointmentType=<sala> de la descripción, conservando el resto */
const conSala = (descripcion, sala) => String(descripcion || '').split('|').map((p) => {
  const i = p.indexOf('=');
  return i > -1 && p.slice(0, i).trim() === 'AppointmentType' ? `${p.slice(0, i + 1)}${sala}` : p;
}).join('|');

const ESTADOS_VIGENTES = ['pending', 'confirmada'];

const SurgeryEditModal = ({ doc, userName, eventos = [], onClose, onSaved }) => {
  const ev = doc?.event || {};
  const fields = parseDesc(ev.description);

  const medico        = val(fields['Medico']);
  const paciente       = val(fields['Paciente']);
  const procedimiento  = val(fields['Procedimiento']);

  // Sala: se puede cambiar por otra sala ACTIVA de la misma unidad
  const unidad     = fields['Unidad Hospitalaroa'] || fields['Unidad Hospitalaria'] || '';
  const salaActual = (fields['AppointmentType'] || '').trim();
  const { salas, loading: cargandoSalas } = useResources(unidad);
  const opcionesSala = useMemo(() => {
    const activas = salas.map((s) => (s.Resource_Name || '').trim()).filter(Boolean);
    // Si la sala actual ya no está activa (p. ej. se desactivó en la migración) se muestra igual
    const yaEsta = activas.some((n) => normTxt(n) === normTxt(salaActual));
    return yaEsta || !salaActual ? activas : [salaActual, ...activas];
  }, [salas, salaActual]);
  const salaActualActiva = salas.some((s) => normTxt(s.Resource_Name) === normTxt(salaActual));
  const [salaVal, setSalaVal] = useState(salaActual);

  const [startVal, setStartVal] = useState(toInputValue(ev.start?.dateTime));
  const [endVal,   setEndVal]   = useState(toInputValue(ev.end?.dateTime));

  const [arcoC,  setArcoC]  = useState(Boolean(ev.imagenologia?.arcoC));
  const [rayosX, setRayosX] = useState(Boolean(ev.imagenologia?.rayoxX));

  const [postOp, setPostOp] = useState(Boolean(ev.muestrapatologica?.postOperatorios));
  const [transOp, setTransOp] = useState(Boolean(ev.muestrapatologica?.trasOperatorios));

  const [materiales, setMateriales] = useState(
    Array.isArray(ev.materialEspecial) ? ev.materialEspecial.map(getDescriptivo).filter(Boolean) : []
  );
  const [nuevoMaterial, setNuevoMaterial] = useState('');

  const [equipos, setEquipos] = useState(
    Array.isArray(ev.rentaEquipos) ? ev.rentaEquipos.map(getDescriptivo).filter(Boolean) : []
  );
  const [nuevoEquipo, setNuevoEquipo] = useState('');

  // Requerimiento especial — misma forma simple (checkbox + texto libre)
  // que ya se usa en el wizard de reservación, en vez de la lista
  // material/renta de equipos anterior.
  const [hadRequerimientoEspecial, setHadRequerimientoEspecial] = useState(
    Boolean(val(ev.requerimientoEspecial) || val(fields['RequerimientoEspecial']))
  );
  const [requerimientoEspecial, setRequerimientoEspecial] = useState(
    val(ev.requerimientoEspecial) || val(fields['RequerimientoEspecial']) || ''
  );

  const MOTIVOS_REAGENDAMIENTO = [
    'Cambio solicitado por el médico',
    'Cambio solicitado por el paciente',
    'Disponibilidad de quirófano',
    'Disponibilidad de material o equipo',
    'Error de captura',
    'Otro',
  ];
  const [motivoReagendamiento, setMotivoReagendamiento] = useState('');
  const [motivoOtro, setMotivoOtro] = useState('');

  const [guardando, setGuardando] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const agregarMaterial = () => {
    const v = nuevoMaterial.trim();
    if (!v) return;
    setMateriales((prev) => [...prev, v]);
    setNuevoMaterial('');
  };
  // eslint-disable-next-line no-unused-vars
  const agregarEquipo = () => {
    const v = nuevoEquipo.trim();
    if (!v) return;
    setEquipos((prev) => [...prev, v]);
    setNuevoEquipo('');
  };

  const handleGuardar = async () => {
    if (!startVal || !endVal) {
      Swal.fire({ icon: 'warning', title: 'Faltan datos', text: 'Indica fecha y hora de inicio y fin.' });
      return;
    }
    if (new Date(endVal) <= new Date(startVal)) {
      Swal.fire({ icon: 'warning', title: 'Rango inválido', text: 'La hora de fin debe ser posterior a la de inicio.' });
      return;
    }
    const cambioSala = normTxt(salaVal) !== normTxt(salaActual);
    if (!salaVal) {
      Swal.fire({ icon: 'warning', title: 'Falta la sala', text: 'Selecciona la sala.' });
      return;
    }
    if (!cambioSala && !salaActualActiva && salas.length > 0) {
      Swal.fire({ icon: 'warning', title: 'Sala no disponible', text: `La sala "${salaActual}" ya no está activa. Selecciona otra sala.` });
      return;
    }
    // Empalme con otra cirugía pendiente o confirmada en la misma sala y unidad
    const ini = new Date(startVal), fin = new Date(endVal);
    const empalme = eventos.find((d) => {
      if (d.id === doc.id) return false;
      const e = d?.event || {};
      if (!ESTADOS_VIGENTES.includes(String(e.summary || '').toLowerCase())) return false;
      const f = parseDesc(e.description);
      if (normTxt(f['Unidad Hospitalaroa'] || f['Unidad Hospitalaria']) !== normTxt(unidad)) return false;
      if (normTxt(f['AppointmentType']) !== normTxt(salaVal)) return false;
      const i2 = new Date(normISO(e.start?.dateTime)), f2 = new Date(normISO(e.end?.dateTime));
      return !isNaN(i2) && !isNaN(f2) && ini < f2 && i2 < fin;
    });
    if (empalme) {
      const f = parseDesc(empalme.event.description);
      Swal.fire({
        icon: 'warning',
        title: 'Horario ocupado',
        text: `"${salaVal}" ya tiene una cirugía de ${fmtHora(empalme.event.start?.dateTime)} a ${fmtHora(empalme.event.end?.dateTime)}${val(f['Procedimiento']) ? ` (${val(f['Procedimiento'])})` : ''}. Elige otra sala u horario.`,
      });
      return;
    }
    if (!motivoReagendamiento) {
      Swal.fire({ icon: 'warning', title: 'Falta el motivo', text: 'Selecciona el motivo del reagendamiento.' });
      return;
    }
    if (motivoReagendamiento === 'Otro' && !motivoOtro.trim()) {
      Swal.fire({ icon: 'warning', title: 'Falta el motivo', text: 'Describe el motivo del reagendamiento.' });
      return;
    }

    setGuardando(true);

    const motivoFinal = motivoReagendamiento === 'Otro' ? motivoOtro.trim() : motivoReagendamiento;

    const eventoClonado = {
      ...ev,
      start: { ...ev.start },
      end: { ...ev.end },
      motivoReagendamiento: motivoFinal,
      requerimientoEspecial: (hadRequerimientoEspecial && requerimientoEspecial.trim()) ? requerimientoEspecial.trim() : null,
    };

    const descripcionNueva = cambioSala ? conSala(ev.description, salaVal) : ev.description;

    const ahora = new Date().toISOString();
    const fechaAnterior       = normISO(ev.start?.dateTime)?.split('T')[0] || '';
    const horaInicialAnterior = fmtHora(ev.start?.dateTime);
    const horaFinalAnterior   = fmtHora(ev.end?.dateTime);

    try {
      const res = await createUpdateCalendarLog(
        doc.id,               // eventId
        'update',              // peticion
        ev.summary || 'Pending', // status
        eventoClonado,          // event
        descripcionNueva,       // AppointmentType (descripción, con la sala nueva si cambió)
        startVal,                // date
        endVal,                  // dateFinal
        userName || fields['Solicitado'] || '', // create
        ahora,                   // time
        doc.id,                  // id
        fechaAnterior,
        horaInicialAnterior,
        horaFinalAnterior,
        { arcoC, rayoxX: rayosX },              // imagenologia
        materiales,                              // materialEspecial
        equipos,                                 // rentaEquipos
        { postOperatorios: postOp, trasOperatorios: transOp }, // muestrapatologica
        ev.pacienteId,
        cambioSala ? { salaAnterior: salaActual, salaNueva: salaVal } : {}
      );

      setGuardando(false);

      if (res === 'success') {
        Swal.fire({ icon: 'success', title: 'Cirugía reagendada', timer: 1600, showConfirmButton: false });
        onSaved && onSaved();
        onClose();
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo reagendar', text: 'Intenta nuevamente.' });
      }
    } catch (err) {
      setGuardando(false);
      Swal.fire({ icon: 'error', title: 'No se pudo reagendar', text: 'Intenta nuevamente.' });
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px',
          maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: '#0582a9', fontSize: '15px', fontWeight: 800 }}>✎ Reagendar cirugía</span>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', color: '#374151', cursor: 'pointer', fontSize: '16px', height: '30px', width: '30px', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', overflowY: 'auto' }}>

          {(medico || paciente || procedimiento) && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '18px', padding: '12px 14px' }}>
              {medico && (
                <div style={{ marginBottom: (paciente || procedimiento) ? '8px' : 0 }}>
                  <IdLabel>Médico</IdLabel>
                  <span style={{ color: '#111827', display: 'block', fontSize: '13px', fontWeight: 700 }}>{medico}</span>
                </div>
              )}
              {paciente && (
                <div style={{ marginBottom: procedimiento ? '8px' : 0 }}>
                  <IdLabel>Paciente</IdLabel>
                  <span style={{ color: '#111827', display: 'block', fontSize: '13px', fontWeight: 700 }}>{paciente}</span>
                </div>
              )}
              {procedimiento && (
                <div>
                  <IdLabel>Procedimiento</IdLabel>
                  <span style={{ color: '#111827', display: 'block', fontSize: '13px', fontWeight: 700 }}>{procedimiento}</span>
                </div>
              )}
            </div>
          )}

          <Label>Sala</Label>
          <select
            value={salaVal}
            onChange={(e) => setSalaVal(e.target.value)}
            disabled={cargandoSalas}
            style={inputStyle}
          >
            {cargandoSalas && <option value={salaVal}>{salaVal || 'Cargando salas…'}</option>}
            {!cargandoSalas && opcionesSala.map((n) => (
              <option key={n} value={n}>
                {n}{normTxt(n) === normTxt(salaActual) ? (salaActualActiva ? ' (actual)' : ' (actual · inactiva)') : ''}
              </option>
            ))}
          </select>
          {normTxt(salaVal) !== normTxt(salaActual) && (
            <span style={{ color: '#0582a9', display: 'block', fontSize: '11px', fontWeight: 600, marginTop: '6px' }}>
              Cambia de "{salaActual}" a "{salaVal}"
            </span>
          )}

          <div style={{ height: '12px' }} />

          <Label>Inicio</Label>
          <input
            type="datetime-local"
            value={startVal}
            onChange={(e) => setStartVal(e.target.value)}
            style={inputStyle}
          />

          <div style={{ height: '12px' }} />

          <Label>Fin</Label>
          <input
            type="datetime-local"
            value={endVal}
            onChange={(e) => setEndVal(e.target.value)}
            style={inputStyle}
          />

          {/* Imagenología / Muestra patológica / Material especial / Renta de
              equipos — ocultos por el momento a petición. No se elimina la
              lógica ni el estado (arcoC, rayosX, postOp, transOp, materiales,
              equipos) — al guardar se siguen enviando tal cual estaban, sin
              perder los datos que ya existieran. Para reactivar, descomentar
              este bloque completo. */}
          {/*
          <div style={{ height: '18px' }} />
          <Label>Imagenología</Label>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px' }}>
            <Checkbox checked={arcoC}  onChange={setArcoC}  label="Arco C" />
            <Checkbox checked={rayosX} onChange={setRayosX} label="Rayos X" />
          </div>

          <div style={{ height: '18px' }} />
          <Label>Muestra patológica</Label>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px' }}>
            <Checkbox checked={postOp}   onChange={setPostOp}   label="Post-operatoria" />
            <Checkbox checked={transOp}  onChange={setTransOp}  label="Trans-operatoria" />
          </div>

          <div style={{ height: '18px' }} />
          <Label>Material especial</Label>
          <ListaEditable
            items={materiales}
            onRemove={(i) => setMateriales((prev) => prev.filter((_, idx) => idx !== i))}
            valor={nuevoMaterial}
            onValorChange={setNuevoMaterial}
            onAgregar={agregarMaterial}
            placeholder="Agregar material…"
          />

          <div style={{ height: '18px' }} />
          <Label>Renta de equipos</Label>
          <ListaEditable
            items={equipos}
            onRemove={(i) => setEquipos((prev) => prev.filter((_, idx) => idx !== i))}
            valor={nuevoEquipo}
            onValorChange={setNuevoEquipo}
            onAgregar={agregarEquipo}
            placeholder="Agregar equipo…"
          />
          */}

          <div style={{ height: '18px' }} />
          <label style={{ alignItems: 'center', color: '#111827', cursor: 'pointer', display: 'flex', fontSize: '13px', fontWeight: 600, gap: '8px' }}>
            <input
              type="checkbox"
              checked={hadRequerimientoEspecial}
              onChange={(e) => setHadRequerimientoEspecial(e.target.checked)}
              style={{ accentColor: '#04afaa', height: '15px', width: '15px' }}
            />
            ¿Requiere de algún Material o Equipo especial?
          </label>
          {hadRequerimientoEspecial && (
            <>
              <div style={{ height: '10px' }} />
              <textarea
                value={requerimientoEspecial}
                onChange={(e) => setRequerimientoEspecial(e.target.value)}
                placeholder="Especifique el material o equipo especial requerido…"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </>
          )}

          <div style={{ height: '18px' }} />
          <Label>Motivo de reagendamiento <span style={{ color: '#e12f3b' }}>*</span></Label>
          <select
            value={motivoReagendamiento}
            onChange={(e) => setMotivoReagendamiento(e.target.value)}
            style={inputStyle}
          >
            <option value="">Selecciona un motivo…</option>
            {MOTIVOS_REAGENDAMIENTO.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          {motivoReagendamiento === 'Otro' && (
            <>
              <div style={{ height: '10px' }} />
              <input
                type="text"
                value={motivoOtro}
                onChange={(e) => setMotivoOtro(e.target.value)}
                placeholder="Describe el motivo…"
                style={inputStyle}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '8px', padding: '14px 22px', borderTop: '1px solid #f3f4f6' }}>
          <button
            onClick={onClose}
            disabled={guardando}
            style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', cursor: 'pointer', fontSize: '12px', fontWeight: 700, padding: '9px 16px' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={guardando}
            style={{
              marginLeft: 'auto', background: '#0582a9', border: 'none', borderRadius: '8px',
              color: '#fff', cursor: guardando ? 'default' : 'pointer', fontSize: '12px', fontWeight: 700,
              padding: '9px 18px', opacity: guardando ? .7 : 1,
            }}
          >
            {guardando ? 'Reagendando…' : 'Reagendar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px',
  padding: '8px 10px', width: '100%', boxSizing: 'border-box',
};

const Label = ({ children }) => (
  <span style={{ color: '#6b7280', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '.5px', marginBottom: '6px', textTransform: 'uppercase' }}>
    {children}
  </span>
);

const IdLabel = ({ children }) => (
  <span style={{ color: '#9ca3af', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '.5px', marginBottom: '2px', textTransform: 'uppercase' }}>
    {children}
  </span>
);

// eslint-disable-next-line no-unused-vars
const Checkbox = ({ checked, onChange, label }) => (
  <label style={{ alignItems: 'center', color: '#111827', cursor: 'pointer', display: 'flex', fontSize: '13px', fontWeight: 600, gap: '6px' }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: '#04afaa', height: '15px', width: '15px' }} />
    {label}
  </label>
);

// eslint-disable-next-line no-unused-vars
const ListaEditable = ({ items, onRemove, valor, onValorChange, onAgregar, placeholder }) => (
  <div>
    {items.length > 0 && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px', marginBottom: '8px' }}>
        {items.map((it, i) => (
          <div key={i} style={{ alignItems: 'center', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#374151', display: 'flex', fontSize: '12px', fontWeight: 600, gap: '6px', justifyContent: 'space-between', padding: '6px 8px' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it}</span>
            <button
              onClick={() => onRemove(i)}
              aria-label="Quitar"
              style={{ background: 'none', border: 'none', color: '#e12f3b', cursor: 'pointer', flexShrink: 0, fontSize: '13px', fontWeight: 700, padding: 0 }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    )}
    <div style={{ display: 'flex', gap: '6px' }}>
      <input
        type="text"
        value={valor}
        onChange={(e) => onValorChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAgregar(); } }}
        placeholder={placeholder}
        style={{ ...inputStyle, flex: 1 }}
      />
      <button
        onClick={onAgregar}
        style={{ background: '#e6f9f8', border: '1px solid #04afaa', borderRadius: '8px', color: '#039490', cursor: 'pointer', fontSize: '12px', fontWeight: 700, padding: '0 14px' }}
      >
        + Agregar
      </button>
    </div>
  </div>
);

export default SurgeryEditModal;